import { useNavigate } from "react-router";
import { Eye, EyeOff, Loader2, ShieldCheck } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import { toast } from "sonner";

import { Logo } from "@/components/common/logo";
import { MotionPage } from "@/components/common/motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAdminAuth } from "@/contexts/AdminAuthContext";
import { adminApi } from "@/lib/api/adminApi";
import { ApiError } from "@/lib/api/client";
import { usePageMeta } from "@/lib/page-meta";

export function AdminLoginRoute() {
  usePageMeta({
    title: "Administrator Sign In — HealNova Admin Portal",
    description:
      "Secure administrator access to the HealNova internal portal for reviewing doctor registrations.",
    robots: "noindex",
  });
  return <AdminLoginPage />;
}

function AdminLoginPage() {
  const { signIn, isAuthenticated } = useAdminAuth();
  const navigate = useNavigate();
  const [secret, setSecret] = useState("");
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isAuthenticated) void navigate("/admin", { replace: true });
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const value = secret.trim();
    if (!value) {
      setError("Enter your administrator secret to continue.");
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      // Validate the secret against the documented pending-doctors endpoint.
      await adminApi.pendingDoctors(value);
      signIn(value);
      toast.success("Signed in as Administrator");
      void navigate("/admin", { replace: true });
    } catch (err) {
      const status = err instanceof ApiError ? err.status : 0;
      if (status === 403) setError("Invalid Administrator Secret.");
      else if (status === 503) setError("Admin tools are currently disabled on the server.");
      else setError("Could not reach the HealNova server. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="relative grid min-h-screen place-items-center overflow-hidden bg-background px-4 py-10">
      <div className="aurora-soft pointer-events-none absolute inset-0" aria-hidden="true" />

      <MotionPage className="relative w-full max-w-md">
        <div className="flex justify-center">
          <Logo />
        </div>

        <div className="card-surface mt-8 p-6 sm:p-8">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/20 bg-primary/8 px-2.5 py-1 text-xs font-semibold text-primary">
            <ShieldCheck className="size-3.5" aria-hidden="true" />
            Administrator · Internal Portal
          </span>

          <h1 className="mt-4 font-display text-2xl font-semibold text-balance">
            Sign in to the Admin Portal
          </h1>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            Enter the administrator secret to review, approve and reject doctor registrations.
          </p>

          <form onSubmit={handleSubmit} className="mt-6 space-y-4" noValidate>
            <div className="space-y-2">
              <Label htmlFor="admin-secret">Administrator Secret</Label>
              <div className="relative">
                <Input
                  id="admin-secret"
                  type={show ? "text" : "password"}
                  value={secret}
                  autoComplete="off"
                  autoFocus
                  onChange={(event) => setSecret(event.target.value)}
                  placeholder="••••••••••••"
                  aria-invalid={Boolean(error)}
                  className="h-11 rounded-xl pr-11"
                />
                <button
                  type="button"
                  onClick={() => setShow((value) => !value)}
                  aria-label={show ? "Hide secret" : "Show secret"}
                  className="absolute top-1/2 right-2 grid size-8 -translate-y-1/2 place-items-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                >
                  {show ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                </button>
              </div>
              {error && (
                <p role="alert" className="text-sm font-medium text-destructive">
                  {error}
                </p>
              )}
            </div>

            <Button type="submit" className="h-11 w-full" disabled={submitting}>
              {submitting && <Loader2 className="size-4 animate-spin" />}
              {submitting ? "Verifying…" : "Sign In"}
            </Button>
          </form>

          <p className="mt-6 border-t border-border pt-4 text-xs leading-relaxed text-muted-foreground">
            Your secret is kept in this browser tab's session storage only and is cleared when the
            tab closes.
          </p>
        </div>
      </MotionPage>
    </main>
  );
}
